import React, { FC, useState } from 'react'
import { ContactDTO } from 'entities/counterparties/model/dtoTypes'
import { Icon } from 'shared/components/Icons'
import { useDispatch } from 'react-redux'
import { Flex } from 'shared/components/Flex'
import { StyledAddButton, StyledTitle } from 'features/Clients/ui/style'
import { Form, Formik } from 'formik'
import { WrapperFormRow } from 'shared/components/WrapperFormRow'
import { FormDataContactsCompany } from 'features/Clients/model/formData'
import { Button } from 'shared/components/Button'
import { closeModal, openModal } from 'shared/redux/slice/modalSlice'
import { CounterpartiesApi } from 'entities/counterparties/api'
import { useGetCounterpartyByIdQuery } from 'entities/counterparties/model'
import { Dispatch, UnknownAction } from '@reduxjs/toolkit'
import { Loader } from 'shared/components/Loader'
import { toast } from 'sonner'
import { ValidateSchemaOrgContact } from 'pages/clientsCreate/model/validateSchemaOrg'

interface ContactsProps {
    currentCounterpartyId: number | null
    contacts: ContactDTO[]
    isEditable: boolean
    addContact: () => void
    setDefaultContacts: (value: ContactDTO[]) => void
    remove: (index: number) => void
}

export const ContactForm: FC<ContactsProps> = ({
    currentCounterpartyId,
    contacts,
    isEditable,
    addContact,
    setDefaultContacts,
    remove,
}) => {
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)

    const { refetch } = useGetCounterpartyByIdQuery(currentCounterpartyId)

    const onDelete = async (id: number, index: number) => {
        remove(index)

        if (!id) {
            return
        }
        setLoading(true)
        await CounterpartiesApi.deleteContactToCounterparty(id)
        await refetch()
        setLoading(false)
    }

    const buildNewContacts = (updated: ContactDTO) => {
        return contacts.map(contact =>
            contact.id === updated.id ? updated : contact,
        )
    }

    const onSave = async (values: ContactDTO) => {
        try {
            setLoading(true)

            if (!values.id) {
                await CounterpartiesApi.addContactToCounterparty({
                    counterparty_id: currentCounterpartyId!,
                    contacts: [values],
                })
                await refetch()
                setDefaultContacts([])
                setLoading(false)
                return
            }
            await CounterpartiesApi.updateContactToCounterparty({
                contacts: buildNewContacts(values),
            })
            await refetch()
            setLoading(false)
        } catch {
            toast.error('Ошибка. Проверьте правильность данных')
        }
    }

    if (loading) {
        return <Loader />
    }

    return (
        <Flex gap={10} flexDirection='column' margin='0 0 20px 0'>
            <Flex justifyContent='space-between' alignItems='center'>
                <StyledTitle>Контактные лица</StyledTitle>
                {isEditable && (
                    <StyledAddButton onClick={addContact}>
                        Добавить контактное лицо{' '}
                        <div className='icon-add'>
                            {' '}
                            <Icon name='plus' />
                        </div>
                    </StyledAddButton>
                )}
            </Flex>
            {contacts?.map((contact, index) => (
                <Formik
                    key={contact.id ?? Math.random().toString()}
                    initialValues={contact}
                    validationSchema={ValidateSchemaOrgContact}
                    enableReinitialize
                    onSubmit={onSave}
                >
                    {({ submitForm, dirty }) => (
                        <Form className='contact-card'>
                            <WrapperFormRow
                                formData={FormDataContactsCompany(
                                    buildButtons(
                                        isEditable,
                                        dirty,
                                        submitForm,
                                        dispatch,
                                        () => onDelete(contact.id, index),
                                    ),
                                ).map(row =>
                                    row.map(item => ({
                                        ...item,
                                        disabled: !isEditable,
                                        placeholder: '',
                                    })),
                                )}
                            />

                            <Flex flex={1} justifyContent='end' gap={10}>
                                {}
                            </Flex>
                        </Form>
                    )}
                </Formik>
            ))}
        </Flex>
    )
}

// TODO: rework as dedicated component
const buildButtons = (
    isEditable: boolean,
    dirty: boolean,
    submitForm: () => Promise<void>,
    dispatch: Dispatch<UnknownAction>,
    onDelete: () => void,
) => {
    return [
        {
            name: 'save',
            label: '',
            placeholder: '',
            type: 'custom',
            render: () =>
                isEditable &&
                dirty && (
                    <Flex alignItems='end'>
                        <Button
                            type='submit'
                            onClick={e => {
                                e?.preventDefault()
                                submitForm().then()
                            }}
                            style={{ width: '39px' }}
                        >
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width='14'
                                height='12'
                                viewBox='0 0 14 12'
                                fill='none'
                            >
                                <path
                                    d='M13 1.00195L4.42857 11.002L1 7.00195'
                                    stroke='white'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </svg>
                        </Button>
                    </Flex>
                ),
            disabled: false,
            required: false,
        },
        {
            name: 'remove',
            label: '',
            placeholder: '',
            type: 'custom',
            render: () =>
                isEditable && (
                    <Flex alignItems='end'>
                        <Button
                            themeType='outline-2'
                            style={{
                                borderColor: '#FC3F1D',
                                background: '#FFF1EF',
                                width: '39px',
                                padding: 0,
                            }}
                            bgLoading='#FC3F1D'
                            onClick={e => {
                                e?.preventDefault()
                                dispatch(
                                    openModal({
                                        isOpen: true,
                                        content: (
                                            <>
                                                Вы точно хотите удалить
                                                контактное лицо?
                                            </>
                                        ),
                                        config: {
                                            title: 'Вы точно хотите удалить?',
                                            btns: [
                                                {
                                                    onClick: () => {
                                                        onDelete()
                                                        dispatch(closeModal())
                                                    },
                                                    title: 'Да',
                                                    active: true,
                                                },
                                                {
                                                    onClick: () =>
                                                        dispatch(closeModal()),
                                                    title: 'Нет',
                                                    active: false,
                                                },
                                            ],
                                        },
                                    }),
                                )
                            }}
                        >
                            <Icon name='delete' />
                        </Button>
                    </Flex>
                ),
            disabled: false,
            required: false,
        },
    ]
}
